import { Component, Output, EventEmitter, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { OverlayPanel } from 'primeng/overlaypanel';

export interface SearchResult {
  label: string;
  icon: string;
  route: string;
  category: string;
  description: string;
  keywords?: string[];
}

@Component({
  selector: 'app-global-search',
  standalone: true,
  imports: [CommonModule, FormsModule, InputTextModule, OverlayPanelModule, ButtonModule, TooltipModule],
  template: `
    <div class="global-search-container">
      <span class="p-input-icon-left">
        <i class="pi pi-search search-icon" (click)="onSearchIconClick($event)"></i>
        <input
          pInputText
          type="text"
          placeholder="Buscar funcionalidades..."
          [(ngModel)]="searchTerm"
          (input)="onSearch($event)"
          (focus)="onFocus($event)"
          (keydown.enter)="onEnter()"
          class="global-search-input"
          [pTooltip]="'Pressione Ctrl+K para busca rápida'"
          tooltipPosition="bottom"
          appendTo="body" />
      </span>

      <p-overlayPanel #op [showCloseIcon]="true" [dismissable]="true" [showTransitionOptions]="'0ms'" [hideTransitionOptions]="'0ms'">
        <div class="search-results" *ngIf="filteredResults.length > 0">
          <div class="search-category" *ngFor="let category of groupedResults | keyvalue">
            <h6 class="category-title">{{ category.key }}</h6>
            <div class="category-items">
              <button
                *ngFor="let result of category.value"
                pButton
                type="button"
                [icon]="result.icon"
                [label]="result.label"
                class="search-result-btn"
                (mousedown)="selectResult(result)">
              </button>
            </div>
          </div>
        </div>
        <div class="no-results" *ngIf="searchTerm && filteredResults.length === 0">
          <i class="pi pi-search"></i>
          <p>Nenhuma funcionalidade encontrada para "{{ searchTerm }}"</p>
        </div>
      </p-overlayPanel>
    </div>
  `,
  styleUrls: ['./global-search.component.scss']
})
export class GlobalSearchComponent {
  @Output() resultSelected = new EventEmitter<SearchResult>();
  @ViewChild('op') overlayPanel!: OverlayPanel;

  searchTerm = '';
  filteredResults: SearchResult[] = [];
  private lastFocusEvent: Event | null = null;

  allResults: SearchResult[] = [
    {
      label: 'Dashboard',
      icon: 'pi pi-home',
      route: '/home',
      category: 'Principal',
      description: 'Página inicial do sistema',
      keywords: ['inicio', 'home', 'painel']
    },
    {
      label: 'Importar Extrato',
      icon: 'pi pi-download',
      route: '/importacao-extrato-bancario',
      category: 'Extratos & Bancos',
      description: 'Importar extratos bancários',
      keywords: ['extrato', 'banco', 'ofx', 'importacao', 'conta corrente']
    },
    {
      label: 'Listar Extratos',
      icon: 'pi pi-list',
      route: '/lista-extrato',
      category: 'Extratos & Bancos',
      description: 'Visualizar extratos importados',
      keywords: ['extrato', 'banco', 'movimentacao', 'listagem']
    },
    {
      label: 'Adicionar Conta Bancária',
      icon: 'pi pi-plus',
      route: '/conta-bancaria/adicionar',
      category: 'Extratos & Bancos',
      description: 'Adicionar nova conta bancária',
      keywords: ['conta', 'banco', 'cadastrar', 'nova conta']
    },
    {
      label: 'Listar Contas Bancárias',
      icon: 'pi pi-file',
      route: '/conta-bancaria/listar',
      category: 'Extratos & Bancos',
      description: 'Gerenciar contas bancárias',
      keywords: ['conta', 'banco', 'listagem']
    },
    {
      label: 'Lançamentos Caixa',
      icon: 'pi pi-cart-plus',
      route: '/listar-lancamento-caixa',
      category: 'Extratos & Bancos',
      description: 'Listar e gerenciar lançamentos de caixa',
      keywords: ['caixa', 'lancamento', 'fluxo', 'dinheiro']
    },
    {
      label: 'Emitir NFS-e',
      icon: 'pi pi-file-export',
      route: '/emitir-nfse',
      category: 'NFS-e',
      description: 'Emitir nota fiscal de serviço',
      keywords: ['nfse', 'nfs-e', 'nota fiscal', 'emissao', 'emitir']
    },
    {
      label: 'Listar NFS-e',
      icon: 'pi pi-filter',
      route: '/listagem-nfs-e',
      category: 'NFS-e',
      description: 'Visualizar NFS-e emitidas',
      keywords: ['nfse', 'nfs-e', 'nota fiscal', 'listagem', 'notas']
    },
    {
      label: 'Importar NFS-e',
      icon: 'pi pi-upload',
      route: '/importacao-nfse-emitidas',
      category: 'NFS-e',
      description: 'Importar NFS-e emitidas fora do sistema',
      keywords: ['nfse', 'nfs-e', 'nota fiscal', 'importacao', 'planilha']
    },
    {
      label: 'Clientes',
      icon: 'pi pi-users',
      route: '/clientes',
      category: 'NFS-e',
      description: 'Gerenciar clientes / tomadores',
      keywords: ['cliente', 'tomador', 'cadastro']
    },
    {
      label: 'Descrição Padrão',
      icon: 'pi pi-align-left',
      route: '/descricao-padrao',
      category: 'NFS-e',
      description: 'Gerenciar descrições padrão de serviços',
      keywords: ['descricao', 'servico', 'padrao', 'texto']
    },
    {
      label: 'Faturamento',
      icon: 'pi pi-dollar',
      route: '/faturamento',
      category: 'Relatórios',
      description: 'Relatório de faturamento',
      keywords: ['relatorio', 'receita', 'fatura']
    },
    {
      label: 'Balancete',
      icon: 'pi pi-wallet',
      route: '/balancete',
      category: 'Relatórios',
      description: 'Relatório balancete',
      keywords: ['relatorio', 'contabil', 'contas']
    },
    {
      label: 'Balanço Patrimonial',
      icon: 'pi pi-money-bill',
      route: '/balanco-patrimonial',
      category: 'Relatórios',
      description: 'Balanço patrimonial',
      keywords: ['relatorio', 'patrimonio', 'ativo', 'passivo']
    },
    {
      label: 'DRE',
      icon: 'pi pi-check-circle',
      route: '/dre',
      category: 'Relatórios',
      description: 'Demonstração do resultado do exercício',
      keywords: ['relatorio', 'resultado', 'lucro', 'demonstracao']
    },
    {
      label: 'Razão',
      icon: 'pi pi-file-check',
      route: '/razao',
      category: 'Relatórios',
      description: 'Razão contábil',
      keywords: ['relatorio', 'contabil', 'lancamentos']
    },
    {
      label: 'Plano de Contas',
      icon: 'pi pi-book',
      route: '/plano-de-contas',
      category: 'Configurações',
      description: 'Gerenciar plano de contas',
      keywords: ['contas', 'contabil', 'configuracao']
    },
    {
      label: 'Dados da Empresa',
      icon: 'pi pi-building',
      route: '/empresa',
      category: 'Configurações',
      description: 'Dados cadastrais da empresa',
      keywords: ['empresa', 'cadastro', 'cnpj', 'configuracao']
    },
    {
      label: 'Sócios',
      icon: 'pi pi-users',
      route: '/socios',
      category: 'Configurações',
      description: 'Gerenciar sócios',
      keywords: ['socio', 'esocial', 'pro labore', 'cadastro']
    },
    {
      label: 'Declarações Fiscais',
      icon: 'pi pi-check-square',
      route: '/listar-declaracoes-fiscais',
      category: 'Fiscal',
      description: 'Listar e gerenciar declarações fiscais',
      keywords: ['declaracao', 'das', 'fiscal', 'obrigacao', 'pgdas']
    },
    {
      label: 'Histórico de Tributos',
      icon: 'pi pi-history',
      route: '/tributos/historico',
      category: 'Fiscal',
      description: 'Histórico de tributos da empresa',
      keywords: ['tributo', 'imposto', 'das', 'inss', 'historico', 'pagamentos']
    },
    {
      label: 'Tributos a Pagar',
      icon: 'pi pi-credit-card',
      route: '/tributos/pagar',
      category: 'Fiscal',
      description: 'Tributos pendentes de pagamento',
      keywords: ['tributo', 'imposto', 'pagar', 'vencimento', 'das', 'inss']
    },
    {
      label: 'Distribuição de Dividendos',
      icon: 'pi pi-chart-line',
      route: '/dividendos',
      category: 'Contábil',
      description: 'Cadastro de dividendos distribuídos aos sócios',
      keywords: ['dividendo', 'distribuicao', 'socio', 'lucro', 'contabil']
    }
  ];

  get groupedResults(): { [key: string]: SearchResult[] } {
    const grouped: { [key: string]: SearchResult[] } = {};
    this.filteredResults.forEach(result => {
      if (!grouped[result.category]) {
        grouped[result.category] = [];
      }
      grouped[result.category].push(result);
    });
    return grouped;
  }

  onSearch(event?: Event): void {
    if (event) {
      this.lastFocusEvent = event;
    }

    const term = this.normalizar(this.searchTerm);
    if (!term) {
      this.filteredResults = [...this.allResults];
      this.mostrarPainel(event);
      return;
    }

    this.filteredResults = this.allResults.filter(result => this.corresponde(result, term));
    this.mostrarPainel(event);
  }

  onFocus(event: Event): void {
    this.lastFocusEvent = event;
    const term = this.normalizar(this.searchTerm);
    this.filteredResults = term
      ? this.allResults.filter(result => this.corresponde(result, term))
      : [...this.allResults];
    this.mostrarPainel(event);
  }

  onEnter(): void {
    if (this.filteredResults.length === 1) {
      this.selectResult(this.filteredResults[0]);
    }
  }

  selectResult(result: SearchResult): void {
    this.resultSelected.emit(result);
    this.searchTerm = '';
    this.filteredResults = [];
    this.overlayPanel?.hide();
  }

  onSearchIconClick(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.lastFocusEvent = event;
    this.onSearch(event);
  }

  private corresponde(result: SearchResult, term: string): boolean {
    const campos = [
      result.label,
      result.description,
      result.category,
      result.route,
      ...(result.keywords ?? [])
    ];

    return campos.some(campo => this.normalizar(campo).includes(term));
  }

  private normalizar(valor: string): string {
    return (valor ?? '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();
  }

  private mostrarPainel(event?: Event): void {
    const targetEvent = event ?? this.lastFocusEvent;
    if (!targetEvent || !this.overlayPanel) {
      return;
    }
    this.overlayPanel.show(targetEvent);
  }
}
